import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";

// Public page — no auth required
export default function PaymentResult() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking | paid | failed | pending

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const source = params.get("source");

    if (source === "stripe") {
      setStatus("paid");
      setTimeout(() => navigate(`/bookings/${bookingId}`), 3000);
    } else {
      setTimeout(() => setStatus("pending"), 1500);
    }
  }, [bookingId, navigate]);

  const ref = bookingId?.slice(0, 8).toUpperCase();

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6">
      {/* Logo */}
      <div className="mb-8">
        <img
          src="https://thebetterfare.com/images/logo.png"
          alt="The Better Fare"
          className="h-10 w-auto"
          onError={e => { e.target.style.display = "none"; }}
        />
      </div>

      <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-10 text-center w-full max-w-sm">
        {status === "checking" && (
          <>
            <div className="w-20 h-20 rounded-full bg-purple-50 flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-purple-400 animate-spin" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">Checking payment…</h1>
            <p className="text-gray-500 text-sm leading-relaxed">Please wait while we confirm your payment.</p>
          </>
        )}

        {(status === "paid" || status === "pending") && (
          <>
            <div className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {status === "paid" ? "Payment Confirmed!" : "Thank you!"}
            </h1>
            {ref && (
              <div className="inline-block bg-purple-50 border border-purple-100 rounded-lg px-4 py-2 mb-4">
                <p className="text-[11px] text-purple-400 uppercase tracking-widest font-semibold mb-0.5">Booking Reference</p>
                <p className="font-mono font-bold text-lg text-purple-800">{ref}</p>
              </div>
            )}
            <p className="text-gray-500 text-sm leading-relaxed mb-6">
              {status === "paid"
                ? "Your e-ticket will be sent to your email shortly."
                : "Your payment has been received. You will receive a confirmation email shortly, followed by your e-ticket."}
            </p>
            {status === "paid" && (
              <button
                onClick={() => navigate(`/bookings/${bookingId}`)}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2.5 rounded-lg text-sm transition-colors">
                Go to Booking →
              </button>
            )}
          </>
        )}

        {status === "failed" && (
          <>
            <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-12 h-12 text-red-400" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">Payment not completed</h1>
            <p className="text-gray-500 text-sm leading-relaxed">
              Something went wrong with your payment. Please contact us and we will send you a new payment link.
            </p>
          </>
        )}
      </div>

      <p className="mt-6 text-xs text-gray-400">
        The Better Fare · <a href="https://thebetterfare.com" className="hover:underline">thebetterfare.com</a>
      </p>
    </div>
  );
}
